import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { Card, Button, Badge, Label } from '@/components/ui';
import { Switch } from '@/components/ui/switch';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';
import { ToggleSection } from '@/components/form/ToggleSection';
import { AnimatePresence } from 'framer-motion';
import type { MealBreakConfig, RateMultiplier, Role } from '@/types';
import type { ExtendedTier } from '@/types/ExtendedTier';
import { CustomizedFieldIndicator } from '@/components/form/CustomizedFieldIndicator';
import { BaseSettingsSection } from './sections/BaseSettingsSection';
import { SurchargeTypeSelector } from './sections/MealSurchargeMethodSelector';
import { MultiplierOffsetSection } from './sections/MultiplierOffsetSection';
import { ExtendedOvertimeSection } from '../shared/ExtendedOvertimeSection';
import { CreditExtraTimeSection } from './sections/CreditExtraTimeSection';
import { FixedFeeSection } from './sections/fixed_fee/FixedFeeSection';
import { HourlyPremiumSection } from '../shared/HourlyPremiumSection';
import { Settings, DollarSign } from 'lucide-react';
import { ConfigSection } from '../shared/ConfigSection';
import { hasAnyHalfDayRoles } from '../shared/utils';
import { SurchargeMethodPanel } from '../shared/surcharge/SurchargeMethodPanel';
import { useMethodSwapAnimation } from '../shared/surcharge/useMethodSwapAnimation';
import { useOvertimeLadderInfo } from '@/hooks/useOvertimeLadderInfo';
import { fieldLabelClass, fieldDescriptionClass } from '@/components/config_forms/shared/surcharge/fieldStyles';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { OffsetConfigSection, type OffsetConfig } from '../shared/OffsetConfigSection';
import { toast } from '@/components/ui/sonner';


interface MealBreakConfigFormProps {
  title: string;
  description: string;
  mealConfig: MealBreakConfig;
  onMealConfigChange: (updates: Partial<MealBreakConfig>) => void;
  context: 'user' | 'client';
  isCustomized?: boolean;
  onRevert?: () => void;
  mealDeadlineHoursProps?: Record<string, unknown>;
  mealDeadlineHoursError?: React.ReactNode;
  gracePeriodMinutesProps?: Record<string, unknown>;
  gracePeriodMinutesError?: React.ReactNode;
  creditIntervalMinutesProps?: Record<string, unknown>;
  creditIntervalMinutesError?: React.ReactNode;
  stepUpAfterMinutesProps?: Record<string, unknown>;
  stepUpAfterMinutesError?: React.ReactNode;
  timeCreditMinutesProps?: Record<string, unknown>;
  timeCreditMinutesError?: React.ReactNode;
  premiumValueProps?: Record<string, unknown>;
  premiumValueError?: React.ReactNode;
  fixedFeeAmountProps?: Record<string, unknown>;
  fixedFeeAmountError?: React.ReactNode;
  halfDayBreaksProps?: Record<string, unknown>;
  halfDayBreaksError?: React.ReactNode;
  noCard?: boolean;
  mealConfigCustomized?: Partial<Record<keyof MealBreakConfig, boolean>>;
  roles?: Role[];
  rateMultipliers?: RateMultiplier[];
  hideToggle?: boolean;
  minimumBillableHours?: number | null;
  halfDayHours?: number | null;
  extendedOvertimeEnabled?: boolean;
  onExtendedOvertimeEnabledChange?: (enabled: boolean) => void;
  extendedOvertimeTiers?: ExtendedTier[];
  onExtendedOvertimeTiersChange?: (tiers: ExtendedTier[]) => void;
  defaultExtendedTiers?: ExtendedTier[];
}

const MealBreakConfigFormComponent: React.FC<MealBreakConfigFormProps> = ({
  title,
  description,
  mealConfig,
  onMealConfigChange,
  context,
  isCustomized,
  onRevert,
  mealDeadlineHoursProps,
  mealDeadlineHoursError,
  gracePeriodMinutesProps,
  gracePeriodMinutesError,
  creditIntervalMinutesProps,
  creditIntervalMinutesError,
  stepUpAfterMinutesProps,
  stepUpAfterMinutesError,
  timeCreditMinutesProps,
  timeCreditMinutesError,
  premiumValueProps,
  premiumValueError,
  fixedFeeAmountProps,
  fixedFeeAmountError,
  halfDayBreaksProps,
  halfDayBreaksError,
  noCard,
  mealConfigCustomized = {},
  roles = [],
  rateMultipliers = [],
  hideToggle,
  minimumBillableHours,
  halfDayHours,
  extendedOvertimeEnabled,
  onExtendedOvertimeEnabledChange,
  extendedOvertimeTiers,
  onExtendedOvertimeTiersChange,
  defaultExtendedTiers,
}) => {
  const [triggerConditionsOpen, setTriggerConditionsOpen] = useState(true);
  const [surchargeConfigOpen, setSurchargeConfigOpen] = useState(true);
  const [fixedFeeStructureMode, setFixedFeeStructureMode] = useState<'single' | 'schedule' | null>(null);

  // Compound key so height animates for BOTH method swaps AND trigger-mode flips.
  // When method is offset_overtime, the inner MultiplierOffsetSection mounts only
  // in deadline mode — flipping trigger mode while staying on offset_overtime would
  // otherwise snap the container height instantly (T1/T2 regression). Including
  // mealTriggerMode in the animation key keeps useMethodSwapAnimation firing.
  const methodSwapKey = `${mealConfig?.mealSurchargeType ?? 'none'}:${mealConfig?.mealTriggerMode ?? 'deadline'}`;
  const {
    containerStyle: methodContainerStyle,
    containerRef: methodContentContainerRef,
    activePanelRef: activeMethodPanelRef,
    fadeDuration: methodFadeDuration,
  } = useMethodSwapAnimation(methodSwapKey);

  // Provide default values if mealConfig is undefined
  const safeConfig = mealConfig || {
    applyMealSurcharge: false,
    applyOnHalfDays: true,
    mealDeadlineHours: null,
    recurringMealDeadline: false,
    gracePeriodMinutes: null,
    breakBillingPolicy: null,
    mealSurchargeType: null,
    useAdditionalOffset: true,
    creditIntervalMinutes: null,
    timeCreditMinutes: null,
    fixedFeeSchedule: null,
    stepUpAfterMinutes: null,
    premiumType: null,
    premiumValue: null,
    premiumBasis: 'STRAIGHT_TIME' as const,
    premiumStacking: 'flat' as const,
    fixedFeeAmount: null,
  };
  
  const { highestOvertimeRate, highestOvertimeTierName, maxExtendedTiers } = useOvertimeLadderInfo(rateMultipliers);

  const { applyMealSurcharge, mealSurchargeType, useAdditionalOffset, fixedFeeSchedule } = safeConfig;


  const feeScheduleString = useMemo(() => {
    const feesWithData = (fixedFeeSchedule && fixedFeeSchedule.length > 0
        ? fixedFeeSchedule.filter(f => f.fee_amount != null && !isNaN(f.fee_amount as number))
        : []);

    if (feesWithData.length === 0) {
      // fallback if filter results in empty array or no schedule
      return ['$25.00', '$50.00', '$75.00'].join(' → ');
    }

    return (feesWithData as { fee_amount: number }[]).map(f => `$${(f.fee_amount || 0).toFixed(2)}`).join(' → ');
  }, [fixedFeeSchedule]);

  const anyHalfDayRoles = useMemo(() => hasAnyHalfDayRoles(roles, safeConfig.applicableRoleIds), [roles, safeConfig.applicableRoleIds]);

  // Count-mode compatibility guard. Two cases:
  // 1. guaranteed_rate is fully incompatible — reset to fixed_fee + toast.
  // 2. offset_overtime is allowed in count mode but its time-gated escalation
  //    fields (use_additional_offset, step_up_after_minutes, extended_overtime_*)
  //    are forbidden by count_mode_offset_no_escalation_check — clear them
  //    silently when the mode flips.
  const lastObservedMethodRef = useRef<MealBreakConfig['mealSurchargeType']>(mealSurchargeType);
  useEffect(() => {
    lastObservedMethodRef.current = mealSurchargeType;
  }, [mealSurchargeType]);
  const didInitRef = useRef(false);
  useEffect(() => {
    if (!didInitRef.current) {
      didInitRef.current = true;
      return;
    }
    if (safeConfig.mealTriggerMode !== 'count') return;

    if (mealSurchargeType === 'guaranteed_rate') {
      onMealConfigChange({
        mealSurchargeType: 'fixed_fee',
        // Reset unified rate-offset fields to fixed_fee's CHECK shape.
        floorSource: 'none',
        overtimeOffset: 0,
        floorTierCode: null,
        floorTierCustomValue: null,
      });
      toast.info(
        'Switched method to Fixed Fee — Guaranteed Rate is not available in count mode.'
      );
      return;
    }

    if (mealSurchargeType === 'offset_overtime') {
      // Preserve the method + overtimeOffset + extended-overtime config; clear
      // only the time-gated escalation fields silently (still forbidden by
      // count_mode_offset_no_escalation_check — extended overtime is permitted).
      const needsClearing =
        safeConfig.useAdditionalOffset === true ||
        safeConfig.stepUpAfterMinutes != null;
      if (needsClearing) {
        onMealConfigChange({
          useAdditionalOffset: false,
          stepUpAfterMinutes: null,
        });
      }
      return;
    }
  }, [
    safeConfig.mealTriggerMode,
    safeConfig.useAdditionalOffset,
    safeConfig.stepUpAfterMinutes,
    mealSurchargeType,
    onMealConfigChange,
  ]);

  // OffsetConfigSection wiring. The shared component reads/writes the 5 unified
  // fields on a flat OffsetConfig payload; map to/from MealBreakConfig's
  // camelCase field names. Rendered only when an offset-family method is
  // selected AND trigger mode is 'deadline' (count mode disables offset).
  const offsetConfig: OffsetConfig = useMemo(() => ({
    floor_source: safeConfig.floorSource ?? 'none',
    overtime_offset: safeConfig.overtimeOffset ?? 0,
    floor_tier_code: safeConfig.floorTierCode ?? null,
    floor_tier_custom_value: safeConfig.floorTierCustomValue ?? null,
  }), [
    safeConfig.floorSource, safeConfig.overtimeOffset, safeConfig.floorTierCode,
    safeConfig.floorTierCustomValue,
  ]);
  const handleOffsetChange = (next: OffsetConfig) => {
    onMealConfigChange({
      floorSource: next.floor_source === 'previous_shift' ? 'none' : next.floor_source,
      overtimeOffset: next.overtime_offset,
      floorTierCode: next.floor_tier_code,
      floorTierCustomValue: next.floor_tier_custom_value,
    });
  };

  // Selector wrapper — when the user picks offset_overtime while overtimeOffset
  // is unset or 0 (both are invalid for offset_overtime via overtime_offset_range_check),
  // seed it to 1 so the CHECK constraint is satisfied and the count-mode UI shows a
  // meaningful initial value.
  const handleMealConfigChange = useCallback(
    (updates: Partial<MealBreakConfig>) => {
      if (
        updates.mealSurchargeType === 'offset_overtime' &&
        (safeConfig.overtimeOffset == null || safeConfig.overtimeOffset === 0)
      ) {
        onMealConfigChange({ ...updates, overtimeOffset: 1 });
      } else {
        onMealConfigChange(updates);
      }
    },
    [safeConfig.overtimeOffset, onMealConfigChange]
  );
  const showOffsetConfig =
    // offset_overtime: shown in both deadline AND count modes
    mealSurchargeType === 'offset_overtime'
    // guaranteed_rate: deadline mode only
    || (mealSurchargeType === 'guaranteed_rate'
        && (safeConfig.mealTriggerMode ?? 'deadline') === 'deadline');

  const formFields = (
    <div className="space-y-3">
      <ConfigSection
        title="Trigger Conditions"
        description="Configure when meal break surcharges apply."
        icon={Settings}

        isOpen={triggerConditionsOpen}
        onOpenChange={setTriggerConditionsOpen}
      >
        <BaseSettingsSection
          context={context}
          mealConfigCustomized={mealConfigCustomized}
          safeConfig={safeConfig}
          onMealConfigChange={onMealConfigChange}
          mealDeadlineHoursProps={mealDeadlineHoursProps}
          mealDeadlineHoursError={mealDeadlineHoursError}
          gracePeriodMinutesProps={gracePeriodMinutesProps}
          gracePeriodMinutesError={gracePeriodMinutesError}
          anyHalfDayRoles={anyHalfDayRoles}
          roles={roles}
          halfDayHours={halfDayHours}
          minimumBillableHours={minimumBillableHours}
          halfDayBreaksProps={halfDayBreaksProps}
          halfDayBreaksError={halfDayBreaksError}
        />
      </ConfigSection>

      <ConfigSection
        title="Surcharge Configuration"
        description="Configure how meal break surcharges are calculated."
        icon={DollarSign}

        isOpen={surchargeConfigOpen}
        onOpenChange={setSurchargeConfigOpen}
      >
        <SurchargeTypeSelector
          context={context}
          mealConfigCustomized={mealConfigCustomized}
          mealSurchargeType={mealSurchargeType}
          onMealConfigChange={handleMealConfigChange}
          safeConfig={safeConfig}
          feeScheduleString={feeScheduleString}
          roles={roles}
          rateMultipliers={rateMultipliers}
          minimumBillableHours={minimumBillableHours}
          mealTriggerMode={safeConfig.mealTriggerMode}
          fixedFeeStructureMode={fixedFeeStructureMode}
          onFixedFeeStructureModeChange={setFixedFeeStructureMode}
        />

        <div
          ref={methodContentContainerRef}
          className="overflow-hidden"
          style={methodContainerStyle}
          data-anim-audit="meal-method-panel-container"
        >
          <div className="relative grid">
            <AnimatePresence mode="sync" initial={false}>
              {mealSurchargeType === 'offset_overtime' && (
                <SurchargeMethodPanel
                  key="ladder_offset"
                  activePanelRef={activeMethodPanelRef}
                  transitionDuration={methodFadeDuration}
                >
                  {showOffsetConfig && (
                    <div className="mb-4">
                      <OffsetConfigSection
                        domain="meal_break"
                        method="offset_overtime"
                        config={offsetConfig}
                        rateMultipliers={rateMultipliers}
                        onChange={handleOffsetChange}
                        extendedOvertimeEnabled={extendedOvertimeEnabled ?? false}
                        extendedOvertimeTiers={extendedOvertimeTiers ?? []}
                      />
                    </div>
                  )}
                  {/* Step-up / Additional Offset are deadline-mode only (time-gated
                      escalation has no analog when triggering on miss count).
                      Extended Overtime is permitted in both modes — it extends the
                      rate ladder, orthogonal to how the surcharge is triggered. */}
                  {(safeConfig.mealTriggerMode ?? 'deadline') === 'deadline' ? (
                    <MultiplierOffsetSection
                      safeConfig={safeConfig}
                      onMealConfigChange={onMealConfigChange}
                      stepUpAfterMinutesProps={stepUpAfterMinutesProps}
                      stepUpAfterMinutesError={stepUpAfterMinutesError}
                      mealConfigCustomized={mealConfigCustomized}
                      roles={roles}
                      minimumBillableHours={minimumBillableHours}
                      context={context}
                      extendedOvertimeEnabled={extendedOvertimeEnabled ?? false}
                      onExtendedOvertimeEnabledChange={onExtendedOvertimeEnabledChange ?? (() => {})}
                      extendedOvertimeTiers={extendedOvertimeTiers ?? []}
                      onExtendedOvertimeTiersChange={onExtendedOvertimeTiersChange ?? (() => {})}
                      highestOvertimeRate={highestOvertimeRate}
                      highestOvertimeTierName={highestOvertimeTierName}
                      defaultTiers={defaultExtendedTiers}
                      maxTiers={maxExtendedTiers}
                    />
                  ) : (
                    <ExtendedOvertimeSection
                      enabled={extendedOvertimeEnabled ?? false}
                      onEnabledChange={onExtendedOvertimeEnabledChange ?? (() => {})}
                      tiers={extendedOvertimeTiers ?? []}
                      onTiersChange={onExtendedOvertimeTiersChange ?? (() => {})}
                      highestOvertimeRate={highestOvertimeRate}
                      highestOvertimeTierName={highestOvertimeTierName}
                      isCustomized={mealConfigCustomized.extendedOvertimeEnabled}
                      context={context}
                      defaultTiers={defaultExtendedTiers}
                      maxTiers={maxExtendedTiers}
                    />
                  )}
                </SurchargeMethodPanel>
              )}

              {mealSurchargeType === 'guaranteed_rate' && (safeConfig.mealTriggerMode || 'deadline') === 'deadline' && (
                <SurchargeMethodPanel
                  key="guaranteed_rate"
                  activePanelRef={activeMethodPanelRef}
                  transitionDuration={methodFadeDuration}
                >
                  <OffsetConfigSection
                    domain="meal_break"
                    method="guaranteed_rate"
                    config={offsetConfig}
                    rateMultipliers={rateMultipliers}
                    onChange={handleOffsetChange}
                    extendedOvertimeEnabled={extendedOvertimeEnabled ?? false}
                    extendedOvertimeTiers={extendedOvertimeTiers ?? []}
                  />
                </SurchargeMethodPanel>
              )}

              {mealSurchargeType === 'credit_extra_time' && (
                <SurchargeMethodPanel
                  key="credit_extra_time"
                  activePanelRef={activeMethodPanelRef}
                  transitionDuration={methodFadeDuration}
                >
                  <CreditExtraTimeSection
                    safeConfig={safeConfig}
                    creditIntervalMinutesProps={creditIntervalMinutesProps}
                    creditIntervalMinutesError={creditIntervalMinutesError}
                    timeCreditMinutesProps={timeCreditMinutesProps}
                    timeCreditMinutesError={timeCreditMinutesError}
                    mealConfigCustomized={mealConfigCustomized}
                    context={context}
                    mealTriggerMode={safeConfig.mealTriggerMode}
                    onMealConfigChange={onMealConfigChange}
                    rateMultipliers={rateMultipliers}
                  />
                </SurchargeMethodPanel>
              )}

              {mealSurchargeType === 'fixed_fee' && (
                <SurchargeMethodPanel
                  key="fixed_fee"
                  activePanelRef={activeMethodPanelRef}
                  transitionDuration={methodFadeDuration}
                >
                  <FixedFeeSection
                    safeConfig={safeConfig}
                    onMealConfigChange={onMealConfigChange}
                    mealConfigCustomized={mealConfigCustomized}
                    context={context}
                    fixedFeeAmountProps={fixedFeeAmountProps}
                    fixedFeeAmountError={fixedFeeAmountError}
                    fixedFeeStructureMode={fixedFeeStructureMode}
                    onFixedFeeStructureModeChange={setFixedFeeStructureMode}
                  />
                </SurchargeMethodPanel>
              )}

              {mealSurchargeType === 'hourly_premium' && (
                <SurchargeMethodPanel
                  key="hourly_premium"
                  activePanelRef={activeMethodPanelRef}
                  transitionDuration={methodFadeDuration}
                >
                  <div className="space-y-4">
                    <HourlyPremiumSection
                      config={safeConfig as React.ComponentProps<typeof HourlyPremiumSection>['config']}
                      onConfigChange={onMealConfigChange}
                      premiumValueProps={premiumValueProps}
                      premiumValueError={premiumValueError}
                      customizedFields={mealConfigCustomized}
                      context={context}
                      {...(safeConfig.mealTriggerMode === 'count' ? {
                        descriptionOverrides: {
                          premiumAmountFixed: "This amount is multiplied by your total shift hours.",
                          premiumAmountFixedOT: "This amount is multiplied by your total shift hours, scaled by overtime rates.",
                          premiumAmountPercentage: "Applied to your non-overtime shift pay — total hours times your hourly rate.",
                          premiumAmountPercentageOT: "Applied to your full shift pay — accounts for overtime rate changes across the shift.",
                        }
                      } : {})}
                    />
                    {safeConfig.mealTriggerMode === 'count' && (
                      <div className="w-full grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 items-center md:grid-cols-2 md:gap-4">
                        <div className="min-w-0 space-y-1">
                          <div className="flex items-center gap-1.5">
                            <span className={fieldLabelClass}>Stack Premiums</span>
                            <CustomizedFieldIndicator isCustomized={mealConfigCustomized.premiumStacking} context={context} />
                          </div>
                          <p className={`${fieldDescriptionClass} hidden md:block`}>
                            Multiply the surcharge by the number of missed breaks.
                          </p>
                        </div>
                        <div className="flex items-center justify-end md:w-full">
                          <Switch
                            id="premium-stacking"
                            checked={(safeConfig.premiumStacking ?? 'flat') === 'stacked'}
                            onCheckedChange={(checked) => onMealConfigChange({ premiumStacking: checked ? 'stacked' : 'flat' })}
                          />
                        </div>
                        <p className={`${fieldDescriptionClass} col-span-2 md:hidden`}>
                          Multiply the surcharge by the number of missed breaks.
                        </p>
                      </div>
                    )}
                  </div>
                </SurchargeMethodPanel>
              )}

            </AnimatePresence>
          </div>
        </div>
      </ConfigSection>
    </div>
  );

  if (hideToggle) {
    return formFields;
  }

  const content = (
    <div className="space-y-2 sm:space-y-3">
        {(title || description) && (
          <div className="space-y-1 text-center">
            {title && (
              <div className="flex items-center justify-center gap-3">
                <h3 className="font-heading uppercase tracking-wide text-lg text-foreground">{title}</h3>
                {isCustomized && <Badge variant="default">{context === 'client' ? 'Client' : 'Profile'} Override</Badge>}
              </div>
            )}
            {description && <p className="text-sm text-muted-foreground">{description}</p>}
          </div>
        )}
        <ToggleSection
          id="applyMealSurcharge"
          label={
            <div className="flex items-center gap-1.5">
              <span>Meal Surcharge</span>
              <CustomizedFieldIndicator isCustomized={mealConfigCustomized.applyMealSurcharge} context={context} />
            </div>
          }
          description={
            context === 'client'
              ? "Apply custom meal break surcharge rules for this client."
              : "Charge a surcharge when meal breaks are missed or late."
          }
          checked={applyMealSurcharge}
          onCheckedChange={(checked) => onMealConfigChange({ applyMealSurcharge: checked })}
          variant="default"
        >
          {formFields}
        </ToggleSection>

        <AnimatedCollapse open={!!(isCustomized && onRevert)}>
          <div className="pt-4 border-t flex justify-center">
            <Button variant="link" className="p-0 h-auto text-muted-foreground" onClick={onRevert}>
              Revert to Profile Defaults
            </Button>
          </div>
        </AnimatedCollapse>
    </div>
  );

  if (noCard) {
    return content;
  }

  return (
    <Card className="animate-fade-in rounded equipment-panel p-3 pb-4 sm:p-8 max-w-3xl mx-auto">
      {content}
    </Card>
  );
};

export const MealBreakConfigForm = React.memo(MealBreakConfigFormComponent);
MealBreakConfigForm.displayName = 'MealBreakConfigForm';
